import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, input, signal } from '@angular/core';

import { Subject } from 'rxjs';

import { FormComponent } from '@camelot/form-basic';
import { CamAbstractInput } from '@camelot/form-model';
import { FontIconComponent } from '@camelot/icons';
import { TranslatePipe } from '@camelot/translation';
import {
  BadgeComponent,
  ButtonComponent,
  CardComponent,
  CardContentComponent,
  CardHeaderComponent,
  CardTitleComponent,
  CultureComponent,
  EmptyComponent,
  ErrorComponent,
  ItemProfileDisplayComponent,
  LinkComponent,
  ListContainerComponent,
  ListElementComponent,
  ListExtraInformationComponent,
  ListTitleComponent,
  LoaderComponent,
  TitleComponent,
  UserLogoComponent,
  UserLogoData,
} from '@camelot/ui';
import { CamBaseComponent, RequestState, sendMail } from '@camelot/utils';

import { User } from '../../services/dto/user';
import { CamMyProfileFormService } from '../../services/form/my-profile-form.service';
import { CamUsersService } from '../../services/users.service';
import { CamTranslationUser } from '../../translation.service';

@Component({
  imports: [
    BadgeComponent,
    ButtonComponent,
    CardComponent,
    CardContentComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CommonModule,
    CultureComponent,
    EmptyComponent,
    ErrorComponent,
    FontIconComponent,
    FormComponent,
    ItemProfileDisplayComponent,
    LinkComponent,
    ListContainerComponent,
    ListElementComponent,
    ListExtraInformationComponent,
    ListTitleComponent,
    LoaderComponent,
    TitleComponent,
    TranslatePipe,
    UserLogoComponent,
  ],
  selector: 'cam-user-my-profile',
  styleUrls: ['./user-my-profile.component.scss'],
  templateUrl: './user-my-profile.component.html',
})
export class UserMyProfileComponent extends CamBaseComponent {
  canModify = input(true);

  canDisplayClient = input(true);

  // Notion propre a Merlin : opt-in, les autres apps ne l'affichent pas.
  canDisplayFunctions = input(false);

  public readonly editRequestState = new RequestState();

  public askValidation$ = new Subject<null>();
  public editMode = signal(false);
  public form = signal<CamAbstractInput[] | null>(null);

  public readonly mailTo = sendMail;

  private _formService = inject(CamMyProfileFormService);
  private _userService = inject(CamUsersService);

  get currentUser$() {
    return this._userService.currentUser.get$();
  }

  constructor() {
    super();
    CamTranslationUser.getInstance();
    this._fetch();
  }

  public getUserLogo(user: User): UserLogoData {
    return {
      firstName: user.firstName,
      lastName: user.lastName,
      picture: user.picture,
      pictureDocumentId: user.pictureDocumentId,
      trigram: user.trigram,
    };
  }

  public toggleEdit(user: User) {
    if (this.editMode()) {
      this.askValidation$.next(null);
    } else {
      this.form.set(this._formService.getMyProfileForm(user));
      this.editMode.set(true);
    }
  }

  public cancelEdit() {
    this.editMode.set(false);
    this.form.set(null);
  }

  public validation(data: any, user: User) {
    if (this.editRequestState.isLoading()) {
      return;
    }
    this.editRequestState.asked();

    this._userService.updateCurrentUser$(this._formService.formatMyProfileForm(data, user)).subscribe({
      complete: () => {
        this.editRequestState.completed();
        this.cancelEdit();
      },
      error: (error: HttpErrorResponse) => {
        this.editRequestState.onError(error.status, error.statusText);
      },
    });
  }

  private _fetch() {
    this.requestState.asked();
    this._userService.fetchCurrentUser$().subscribe({
      complete: () => this.requestState.completed(),
      error: (error: HttpErrorResponse) => {
        this.requestState.onError(error.status, error.statusText);
      },
    });
  }
}
